import { TrendingUp, TrendingDown, AlertTriangle, Clock, BarChart2, Percent } from 'lucide-react';
import { cur } from '@/lib/currency';

function fmt(n, decimals = 0) {
  return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function MiniChart({ data = [] }) {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d.amount), 1);
  return (
    <div className="flex items-end gap-0.5 h-8 mt-1">
      {data.map((d, i) => (
        <div key={i} className="flex-1 bg-white/30 rounded-sm transition-all"
          style={{ height: `${Math.max(4, (d.amount / max) * 100)}%` }} />
      ))}
    </div>
  );
}

function StatCard({ children, className = '' }) {
  return (
    <div className={`rounded-2xl p-4 flex-shrink-0 w-52 ${className}`}>
      {children}
    </div>
  );
}

export default function CollectionsDashboard({ stats, onWeekDueClick, onOverdueClick }) {
  if (!stats) return (
    <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="w-52 flex-shrink-0 h-28 bg-muted animate-pulse rounded-2xl" />
      ))}
    </div>
  );

  // The backend (sellgo_coll_dashboard) returns FLAT keys
  // (today_collected, week_due, total_overdue, overdue_customers,
  // efficiency_pct...), but this component reads nested objects
  // (today.amount, overdue.amount, ...). That mismatch made every card read 0.
  // We map the flat keys here, and still accept a nested shape if a future
  // backend sends one. Defensive zero defaults keep it crash-proof.
  const today = stats.today || {
    amount: Number(stats.today_collected) || 0,
    count: Number(stats.today_count) || 0,
    vs_yesterday: Number(stats.today_vs_yesterday) || 0,
    chart: stats.today_chart || [],
  };
  const week_due = (stats.week_due && typeof stats.week_due === 'object') ? stats.week_due : {
    amount: Number(stats.week_due) || 0,
    count: Number(stats.week_due_count) || 0,
  };
  const overdue = stats.overdue || {
    amount: Number(stats.total_overdue) || 0,
    customers: Number(stats.overdue_customers) || 0,
    oldest_days: Number(stats.overdue_oldest_days) || 0,
  };
  const efficiency = stats.efficiency || {
    pct: Number(stats.efficiency_pct) || 0,
    last_month_pct: Number(stats.efficiency_last_month_pct) || 0,
  };
  const todayTrend = Number(today.vs_yesterday) || 0;
  const effPct = Number(efficiency.pct) || 0;
  const effLastPct = Number(efficiency.last_month_pct) || 0;
  const effColor = effPct >= 90 ? 'from-emerald-600 to-emerald-500' : effPct >= 70 ? 'from-amber-600 to-amber-500' : 'from-red-600 to-red-500';

  return (
    <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
      {/* Card 1: Today */}
      <StatCard className="bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold opacity-80">محصل اليوم</p>
          {todayTrend >= 0
            ? <div className="flex items-center gap-0.5 text-xs text-emerald-200"><TrendingUp className="w-3 h-3" />+{fmt(todayTrend)}</div>
            : <div className="flex items-center gap-0.5 text-xs text-red-200"><TrendingDown className="w-3 h-3" />{fmt(todayTrend)}</div>
          }
        </div>
        <p className="text-xl font-black">{fmt(today.amount)} {cur()}</p>
        <p className="text-xs opacity-70">{today.count} إيصال</p>
        <MiniChart data={today.chart} />
      </StatCard>

      {/* Card 2: Week Due */}
      <StatCard className="bg-gradient-to-br from-blue-600 to-blue-500 text-white cursor-pointer" onClick={onWeekDueClick}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold opacity-80">مستحق هذا الأسبوع</p>
          <Clock className="w-3.5 h-3.5 opacity-60" />
        </div>
        <p className="text-xl font-black">{fmt(week_due.amount)} {cur()}</p>
        <p className="text-xs opacity-70">{week_due.count} فاتورة</p>
        <div className="mt-2">
          <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 font-medium">عرض التفاصيل</span>
        </div>
      </StatCard>

      {/* Card 3: Overdue */}
      <StatCard className="bg-gradient-to-br from-red-600 to-red-500 text-white cursor-pointer" onClick={onOverdueClick}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold opacity-80">المتأخرات</p>
          <AlertTriangle className="w-3.5 h-3.5 opacity-80" />
        </div>
        <p className="text-xl font-black">{fmt(overdue.amount)} {cur()}</p>
        <p className="text-xs opacity-70">{overdue.customers} عميل</p>
        {overdue.oldest_days > 0 && (
          <p className="text-xs opacity-60 mt-1">أقدم: {overdue.oldest_days} يوم</p>
        )}
        <div className="mt-1">
          <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 font-medium">إجراء عاجل</span>
        </div>
      </StatCard>

      {/* Card 4: Efficiency */}
      <StatCard className={`bg-gradient-to-br ${effColor} text-white`}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold opacity-80">كفاءة التحصيل</p>
          <Percent className="w-3.5 h-3.5 opacity-60" />
        </div>
        <p className="text-2xl font-black">{effPct}%</p>
        <p className="text-xs opacity-70">
          {effPct >= effLastPct ? '↑' : '↓'} {Math.abs(effPct - effLastPct)}% عن الشهر الماضي
        </p>
        <div className="w-full bg-white/20 rounded-full h-1.5 mt-2">
          <div className="h-full bg-white rounded-full" style={{ width: `${Math.min(100, effPct)}%` }} />
        </div>
      </StatCard>
    </div>
  );
}